/* AdminPush — Web Push notification management.
 *
 * Shows current subscriber stats, a manual broadcast form (with province
 * targeting), and the recent broadcast history.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, BellRing, Users, MapPin, RefreshCw, AlertCircle, BellPlus } from "lucide-react";
import { AdminPageHeader, Field, TextArea, Select } from "./_helpers";
import {
  isPushSupported,
  currentPermission,
  subscribeToPush,
  getCurrentSubscription,
} from "@/lib/push";

export default function AdminPush() {
  const [stats, setStats] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    title: "",
    body: "",
    url: "",
    province_id: "",
    image: "",
  });
  const [sending, setSending] = useState(false);
  const [myPushState, setMyPushState] = useState({ supported: null, permission: "default", subscribed: false });
  const [testing, setTesting] = useState(false);

  const refreshMyPush = async () => {
    const supported = isPushSupported();
    if (!supported) {
      setMyPushState({ supported: false, permission: "unsupported", subscribed: false });
      return;
    }
    let subscribed = false;
    try {
      const s = await getCurrentSubscription();
      subscribed = !!s;
    } catch { /* ignore */ }
    setMyPushState({ supported: true, permission: currentPermission(), subscribed });
  };

  useEffect(() => { refreshMyPush(); }, []);

  const loadAll = async () => {
    try {
      const [s, h, p] = await Promise.all([
        api.get("/push/admin/stats"),
        api.get("/push/admin/history?limit=30"),
        api.get("/provinces?active_only=false"),
      ]);
      setStats(s.data);
      setHistory(h.data.items || []);
      setProvinces(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yüklenemedi");
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const provLookup = useMemo(() => {
    const m = {};
    provinces.forEach((p) => (m[p.id] = p.name));
    return m;
  }, [provinces]);

  const targetCount = useMemo(() => {
    if (!stats) return 0;
    if (!form.province_id) return stats.active || 0;
    const row = (stats.by_province || []).find((r) => r.province_id === form.province_id);
    const globalRow = (stats.by_province || []).find((r) => !r.province_id);
    return (row?.count || 0) + (globalRow?.count || 0);
  }, [stats, form.province_id]);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      return toast.error("Başlık ve içerik zorunlu");
    }
    setSending(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        url: form.url || null,
        image: form.image || null,
        province_id: form.province_id || null,
      };
      const r = await api.post("/push/admin/send", payload);
      toast.success(
        `Gönderildi — başarı: ${r.data.success_count}/${r.data.total}` +
          (r.data.failure_count ? `, başarısız: ${r.data.failure_count}` : "")
      );
      setForm({ title: "", body: "", url: "", province_id: "", image: "" });
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-push-page">
      <AdminPageHeader
        title="Push Bildirimleri"
        subtitle="Web Push (VAPID) ile son dakika ve duyuru bildirimleri"
        action={
          <button
            onClick={loadAll}
            className="px-3 py-2 text-sm border border-gray-200 rounded-md flex items-center gap-1 hover:bg-gray-50"
            data-testid="admin-push-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Yenile
          </button>
        }
      />

      {/* Diagnostic / self-test panel.
       * Push doesn't visibly "work" when 0 subscribers exist. This block
       * surfaces the admin's own browser state and provides a one-click
       * flow to subscribe + send a test ping, so they can verify the
       * entire chain end-to-end without hunting through browser menus. */}
      <div
        className="mb-5 border rounded-lg p-4 bg-white"
        data-testid="admin-push-selftest"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div
            className={`p-2 rounded ${
              myPushState.subscribed
                ? "bg-emerald-100 text-emerald-700"
                : myPushState.permission === "denied"
                ? "bg-rose-100 text-rose-600"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            <BellPlus className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-bold text-sm">
              Bu tarayıcının durumu:{" "}
              {myPushState.supported === false && (
                <span className="text-rose-600">Push desteklenmiyor</span>
              )}
              {myPushState.supported !== false && myPushState.permission === "denied" && (
                <span className="text-rose-600">Engellenmiş</span>
              )}
              {myPushState.supported !== false && myPushState.permission !== "denied" && myPushState.subscribed && (
                <span className="text-emerald-700">Abone</span>
              )}
              {myPushState.supported !== false && myPushState.permission !== "denied" && !myPushState.subscribed && (
                <span className="text-amber-700">Abone değil</span>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-0.5">
              {stats?.active === 0 ? (
                <>Henüz hiçbir cihaz bildirim almak için izin vermemiş. Önce buradan <strong>kendi tarayıcınıza abone olup bir test bildirimi</strong> göndererek sistemin uçtan uca çalıştığını doğrulayın; ardından ziyaretçileri sağ üstteki zil ikonundan izin vermeye yönlendirin.</>
              ) : (
                <>Sistem bu an itibarıyla <strong>{stats?.active ?? "—"}</strong> aktif aboneye yayın yapabilir. Kendi tarayıcınıza test bildirimi atmak için düğmelere basın.</>
              )}
            </p>
            {myPushState.permission === "denied" && (
              <p className="text-[11px] text-rose-600 mt-1">
                Tarayıcınız bildirim iznini reddetmiş. Adres çubuğundaki 🔒 → "Site ayarları" → "Bildirimler" → "İzin ver"i seçip sayfayı yenileyin.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!myPushState.subscribed && myPushState.supported !== false && myPushState.permission !== "denied" && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await subscribeToPush({ provinceId: null });
                    await refreshMyPush();
                    await loadAll();
                    toast.success("Bu tarayıcı başarıyla abone oldu");
                  } catch (e) {
                    toast.error(e?.message || "Abone olunamadı");
                  }
                }}
                className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700"
                data-testid="admin-push-subscribe-self"
              >
                Bu Tarayıcıyı Abone Yap
              </button>
            )}
            <button
              type="button"
              onClick={async () => {
                setTesting(true);
                try {
                  // Make sure we're subscribed first so the test actually
                  // reaches us; subscribeToPush is idempotent.
                  if (myPushState.supported !== false && myPushState.permission !== "denied") {
                    try {
                      await subscribeToPush({ provinceId: null });
                      await refreshMyPush();
                    } catch { /* best-effort */ }
                  }
                  const r = await api.post("/push/admin/send", {
                    title: "SİNE81 Test",
                    body: `Push altyapısı çalışıyor — ${new Date().toLocaleTimeString("tr-TR")}`,
                    url: "/",
                  });
                  if ((r.data.success_count || 0) > 0) {
                    toast.success(`Test gönderildi — ${r.data.success_count}/${r.data.total} ulaştı`);
                  } else if ((r.data.total || 0) === 0) {
                    toast.error("Hiç abone yok — önce kendinizi abone yapın");
                  } else {
                    toast.error(`Gönderildi ama ${r.data.failure_count} hata — Hata sebepleri 'Pasif' istatistiğinde görünür`);
                  }
                  await loadAll();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Test gönderilemedi");
                } finally {
                  setTesting(false);
                }
              }}
              disabled={testing}
              className="px-3 py-1.5 text-sm border rounded font-semibold hover:bg-gray-50 disabled:opacity-50"
              data-testid="admin-push-test-send"
            >
              {testing ? "Gönderiliyor…" : "Test Bildirimi Gönder"}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard
          icon={<Users className="w-5 h-5" />}
          label="Aktif Abone"
          value={stats?.active ?? "—"}
          accent
          testId="stat-active"
        />
        <StatCard
          icon={<BellRing className="w-5 h-5" />}
          label="Toplam Kayıt"
          value={stats?.total ?? "—"}
          testId="stat-total"
        />
        <StatCard
          icon={<AlertCircle className="w-5 h-5" />}
          label="Pasif (iptal/hata)"
          value={stats?.inactive ?? "—"}
          testId="stat-inactive"
        />
        <StatCard
          icon={<MapPin className="w-5 h-5" />}
          label="Hedeflenecek"
          value={targetCount}
          accent
          testId="stat-target"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Compose */}
        <div className="lg:col-span-2 bg-white border border-gray-100 rounded-lg p-5 space-y-3">
          <div>
            <h3 className="font-black text-lg">Yeni Bildirim Yayınla</h3>
            <p className="text-xs text-gray-500">
              Son dakika haberi yayınladığınızda otomatik olarak Push bildirimi gider. Buradan
              manuel duyuru gönderebilirsiniz.
            </p>
          </div>
          <Field
            label="Başlık (en fazla 120 karakter)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            maxLength={120}
            data-testid="admin-push-title"
          />
          <TextArea
            rows={3}
            label="Mesaj (en fazla 400 karakter)"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            maxLength={400}
            data-testid="admin-push-body"
          />
          <div className="grid md:grid-cols-2 gap-3">
            <Field
              label="Yönlendirme URL'i (isteğe bağlı)"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="/haber/ornek-haber"
              data-testid="admin-push-url"
            />
            <Field
              label="Görsel URL'i (isteğe bağlı)"
              value={form.image}
              onChange={(e) => setForm({ ...form, image: e.target.value })}
              placeholder="/api/media/file/..."
            />
          </div>
          <Select
            label="Hedef İl (boş = tüm aboneler)"
            value={form.province_id}
            options={[
              { value: "", label: "Tüm Türkiye (Genel)" },
              ...provinces.map((p) => ({ value: p.id, label: p.name })),
            ]}
            onChange={(e) => setForm({ ...form, province_id: e.target.value })}
            data-testid="admin-push-province"
          />
          <div className="flex items-center justify-between border-t pt-3">
            <div className="text-xs text-gray-500">
              Tahmini hedef: <strong>{targetCount}</strong> abone
            </div>
            <button
              onClick={send}
              disabled={sending}
              className="px-5 py-2 bg-[#e11d2a] text-white rounded-md text-sm font-bold flex items-center gap-1 disabled:opacity-50 hover:bg-[#c01825]"
              data-testid="admin-push-send"
            >
              <Send className="w-4 h-4" />
              {sending ? "Gönderiliyor..." : "Yayınla"}
            </button>
          </div>
        </div>

        {/* By-province distribution */}
        <div className="bg-white border border-gray-100 rounded-lg p-5">
          <h3 className="font-black text-lg mb-2">İl Bazında Dağılım</h3>
          {stats?.by_province?.length ? (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {stats.by_province.map((row) => (
                <div
                  key={row.province_id || "global"}
                  className="flex items-center justify-between text-sm py-1 border-b border-gray-50 last:border-0"
                >
                  <span>{row.province_id ? provLookup[row.province_id] || "—" : "Genel"}</span>
                  <span className="font-mono font-bold">{row.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-500">Henüz abone yok.</div>
          )}
        </div>
      </div>

      {/* History */}
      <div className="mt-6 bg-white border border-gray-100 rounded-lg overflow-x-auto">
        <div className="px-5 py-3 border-b">
          <h3 className="font-black text-lg">Son Yayınlar</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2">Tarih</th>
              <th className="px-3 py-2">Başlık</th>
              <th className="px-3 py-2">Kaynak</th>
              <th className="px-3 py-2">Hedef</th>
              <th className="px-3 py-2 text-right">Başarı</th>
              <th className="px-3 py-2 text-right">Hata</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                  {new Date(h.sent_at).toLocaleString("tr-TR")}
                </td>
                <td className="px-3 py-2 max-w-md truncate" title={h.title}>
                  <div className="font-semibold truncate">{h.title}</div>
                  <div className="text-xs text-gray-500 truncate">{h.body}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {h.source === "breaking_news" ? (
                    <span className="px-1.5 py-0.5 rounded bg-red-100 text-[#e11d2a] font-bold">SON DAKİKA</span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded bg-gray-100">Manuel</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {h.province_id ? provLookup[h.province_id] || "—" : "Tüm"}
                </td>
                <td className="px-3 py-2 text-right font-mono text-green-600">
                  {h.success_count}/{h.total}
                </td>
                <td className="px-3 py-2 text-right font-mono text-red-500">
                  {h.failure_count || 0}
                </td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr>
                <td colSpan="6" className="p-8 text-center text-gray-500">
                  Henüz yayın yok
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`bg-white border ${
        accent ? "border-[#e11d2a]/30" : "border-gray-100"
      } rounded-lg p-4 flex items-center gap-3`}
    >
      <div className={`p-2 rounded ${accent ? "bg-[#e11d2a]/10 text-[#e11d2a]" : "bg-gray-100 text-gray-600"}`}>
        {icon}
      </div>
      <div>
        <div className="text-2xl font-black leading-tight">{value}</div>
        <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      </div>
    </div>
  );
}
